import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { api } from "@/lib/api";
import Layout from "@/components/layout/Layout";
import HomePage from "@/pages/HomePage";
import CategoryPage from "@/pages/CategoryPage";
import ArticlePage from "@/pages/ArticlePage";
import SearchResultsPage from "@/pages/SearchResultsPage";
import ContactPage from "@/pages/ContactPage";
import AboutPage from "@/pages/AboutPage";
import PrivacyPage from "@/pages/PrivacyPage";
import NotFoundPage from "@/pages/NotFoundPage";

import ProtectedRoute from "@/components/admin/ProtectedRoute";
import ControlApp from "@/control/ControlApp";
import AdminLayout from "@/components/admin/AdminLayout";
import useGoogleAnalytics from "@/lib/useGoogleAnalytics";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminArticlesListPage from "@/pages/admin/AdminArticlesListPage";
import AdminArticleEditorPage from "@/pages/admin/AdminArticleEditorPage";
import AdminCategoriesPage from "@/pages/admin/AdminCategoriesPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";
import AdminMediaPage from "@/pages/admin/AdminMediaPage";
import AdminSocialImagesPage from "@/pages/admin/AdminSocialImagesPage";
import AdminHomepageManagerPage from "@/pages/admin/AdminHomepageManagerPage";
import AdminAdvertisingPage from "@/pages/admin/AdminAdvertisingPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminAuditPage from "@/pages/admin/AdminAuditPage";
import AdminStatsPage from "@/pages/admin/AdminStatsPage";
import AdminAutomationPage from "@/pages/admin/AdminAutomationPage";
import AdminHealthPage from "@/pages/admin/AdminHealthPage";
import AdminEditorialEnginePage from "@/pages/admin/AdminEditorialEnginePage";
import AdminSocialSettingsPage from "@/pages/admin/AdminSocialSettingsPage";
import AdminChangePasswordPage from "@/pages/admin/AdminChangePasswordPage";

function PublicShell({ children }) {
  return <Layout>{children}</Layout>;
}

function AdminShell({ children, require }) {
  return (
    <ProtectedRoute require={require}>
      <AdminLayout>{children}</AdminLayout>
    </ProtectedRoute>
  );
}

function AppRoutes() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isControl = location.pathname.startsWith("/control");
  // Google Analytics 4 — public pages only. Loads gtag.js once + tracks route changes.
  useGoogleAnalytics(!isAdmin && !isControl);
  if (isControl) {
    return (
      <Routes>
        <Route path="/control/*" element={<ControlApp />} />
      </Routes>
    );
  }
  if (isAdmin) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<AdminShell><AdminDashboardPage /></AdminShell>} />
        <Route path="/admin/cambiar-contrasena" element={<AdminShell><AdminChangePasswordPage /></AdminShell>} />
        <Route path="/admin/noticias" element={<AdminShell require={["articles.create","articles.edit_any","articles.edit_own","articles.approve","articles.publish"]}><AdminArticlesListPage /></AdminShell>} />
        <Route path="/admin/noticias/nueva" element={<AdminShell require="articles.create"><AdminArticleEditorPage /></AdminShell>} />
        <Route path="/admin/noticias/:id" element={<AdminShell require={["articles.create","articles.edit_any","articles.edit_own"]}><AdminArticleEditorPage /></AdminShell>} />
        <Route path="/admin/categorias" element={<AdminShell require="categories.manage"><AdminCategoriesPage /></AdminShell>} />
        <Route path="/admin/portada" element={<AdminShell require="homepage.manage"><AdminHomepageManagerPage /></AdminShell>} />
        <Route path="/admin/publicidad" element={<AdminShell require={["ads.manage","ads.stats"]}><AdminAdvertisingPage /></AdminShell>} />
        <Route path="/admin/multimedia" element={<AdminShell require={["media.upload","media.manage"]}><AdminMediaPage /></AdminShell>} />
        <Route path="/admin/editor-fotos" element={<AdminShell require="articles.create"><AdminSocialImagesPage /></AdminShell>} />
        <Route path="/admin/usuarios" element={<AdminShell require={["users.manage","users.manage_all"]}><AdminUsersPage /></AdminShell>} />
        <Route path="/admin/estadisticas" element={<AdminShell require={["stats.view","ads.stats"]}><AdminStatsPage /></AdminShell>} />
        <Route path="/admin/automatizacion" element={<AdminShell require={["automation.view","automation.manage"]}><AdminAutomationPage /></AdminShell>} />
        <Route path="/admin/estado-sistema" element={<AdminShell require={["settings.manage","users.manage_all","users.manage","automation.manage","automation.view"]}><AdminHealthPage /></AdminShell>} />
        <Route path="/admin/motor-editorial" element={<AdminShell require="automation.manage"><AdminEditorialEnginePage /></AdminShell>} />
        <Route path="/admin/redes-sociales" element={<AdminShell require={["settings.manage","articles.publish","articles.create","automation.manage"]}><AdminSocialSettingsPage /></AdminShell>} />
        <Route path="/admin/auditoria" element={<AdminShell require="audit.view"><AdminAuditPage /></AdminShell>} />
        <Route path="/admin/configuracion" element={<AdminShell require="settings.manage"><AdminSettingsPage /></AdminShell>} />
        <Route path="/admin/*" element={<AdminShell><AdminDashboardPage /></AdminShell>} />
      </Routes>
    );
  }
  return (
    <PublicShell>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/categoria/:slug" element={<CategoryPage />} />
        <Route path="/noticia/:slug" element={<ArticlePage />} />
        <Route path="/buscar" element={<SearchResultsPage />} />
        <Route path="/contacto" element={<ContactPage />} />
        <Route path="/quienes-somos" element={<AboutPage />} />
        <Route path="/politicas-de-privacidad" element={<PrivacyPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </PublicShell>
  );
}

function GlobalSEOInjector() {
  // Injects site-wide meta tags (google verification, GA snippet) once at load.
  useEffect(() => {
    let cancelled = false;
    api.seoSite().then((s) => {
      if (cancelled || !s) return;
      if (s.google_site_verification) {
        let tag = document.head.querySelector('meta[name="google-site-verification"]');
        if (!tag) {
          tag = document.createElement("meta");
          tag.setAttribute("name", "google-site-verification");
          document.head.appendChild(tag);
        }
        tag.setAttribute("content", s.google_site_verification);
      }
      if (s.google_analytics_id && !document.getElementById("__ga_script")) {
        const s1 = document.createElement("script");
        s1.async = true;
        s1.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(s.google_analytics_id)}`;
        s1.id = "__ga_script";
        document.head.appendChild(s1);
        const s2 = document.createElement("script");
        s2.textContent = `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${s.google_analytics_id}');`;
        document.head.appendChild(s2);
      }
      // Add RSS/atom link discovery
      if (!document.head.querySelector('link[rel="alternate"][type="application/rss+xml"][data-global="1"]')) {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("type", "application/rss+xml");
        link.setAttribute("title", `${s.site_name || "Sur Primicia"} - RSS`);
        link.setAttribute("href", "/api/rss.xml");
        link.setAttribute("data-global", "1");
        document.head.appendChild(link);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <GlobalSEOInjector />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

export default App;
